import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  CircularProgress,
} from '@material-ui/core';
import { Close as CloseIcon } from '@material-ui/icons';
import axios from 'axios';
import envConfig from '../../../bin/env.config';
import StoreDateStatusCell from './StoreDateStatusCell';
import { BACKGROUND_STATUS_COLORS } from '../../../utility/statusConfig';
import { getStatsTooltipText } from './oodMetricConfig';

function StoreCheckRow({ row, datePeriod }) {
  const isNotUpdated = !row.last_collected_date
    || new Date(row.last_collected_date) < new Date(Date.now() - datePeriod * 24 * 60 * 60 * 1000);
  const [cellColor, setCellColor] = React.useState(isNotUpdated ? BACKGROUND_STATUS_COLORS[0] : '');

  return (
    <TableRow>
      <TableCell>{row.store_name}</TableCell>
      <TableCell>{row.store_postal}</TableCell>
      <TableCell>{row.city}</TableCell>
      <TableCell
        style={{
          backgroundColor: cellColor,
          color: isNotUpdated ? '#fff' : '#000',
        }}
      >
        <StoreDateStatusCell
          cellData={{
            company_product_id: row.company_product_id,
            store_id: row.store_id,
            date: row.last_collected_date,
            not_updated: isNotUpdated ? 1 : 0,
            external_link: row.external_link,
          }}
          setCellColor={setCellColor}
        />
      </TableCell>
    </TableRow>
  );
}

export default function OodCellDetailDialog({
  open,
  onClose,
  stats,
  pmgId,
  productName,
  bannerName,
  userTypeId,
  selectedCity,
  selectedBanners,
  token,
  datePeriod,
}) {
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !stats) return;

    const fetchStores = async () => {
      try {
        setLoading(true);
        const response = await axios.post(
          `${envConfig.API_ROOT}${envConfig.API_VERSION}/company-report-tool/data-freshness-reporting/out-of-date-store-summary`,
          {
            user_type_id: userTypeId,
            city: selectedCity,
            banners: selectedBanners,
          },
          { headers: { Authorization: token } }
        );
        const companyId = stats.company_id;
        const filtered = (response.data.data || []).filter(
          (r) => String(r.pmg_id) === String(pmgId) && String(r.company_id) === String(companyId)
        );
        setStores(filtered);
      } catch (e) {
        console.error(e);
        setStores([]);
      } finally {
        setLoading(false);
      }
    };

    fetchStores();
  }, [open, stats, pmgId, userTypeId, selectedCity, selectedBanners, token]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <span>
            PMG {pmgId} · {bannerName}
          </span>
          <IconButton onClick={onClose} aria-label="close">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography variant="subtitle2" gutterBottom>
          {productName}
        </Typography>
        <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
          {getStatsTooltipText(stats)}
        </Typography>
        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Store</TableCell>
                <TableCell>Postal</TableCell>
                <TableCell>City</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {stores.map((row) => (
                <StoreCheckRow key={`${row.store_id}-${row.company_product_id}`} row={row} datePeriod={datePeriod} />
              ))}
              {stores.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    No stores found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </DialogContent>
    </Dialog>
  );
}
